import { useContext } from 'react';
import type { PartialDeep } from 'type-fest';

import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';
import type { TenantSettings } from '@core/types/api/TenantSettings/TenantSettings';
import { NewUpdateTenantSettingsRequest } from '@core/types/api/UpdateTenantSettingsRequest/UpdateTenantSettingsRequest';

import { useSlabMutation } from './useSlabMutation';

export type MarginConfiguration = Enums.MarginConfiguration;

type TenantMarginSettings = {
  marginConfiguration: MarginConfiguration;
  defaultMargins: {
    targetMarginFlatAmount: string;
    minimumMarginFlatAmount: string;
    targetMarginPercentage: string;
    minimumMarginPercentage: string;
  };
  defaultIsMoneySwitchOn: boolean;
  saveTenantMargins: (margins: PartialDeep<TenantSettings>) => Promise<TenantSettings>;
};

/**
 * Custom hook to fetch and manage tenant settings
 */
export const useTenantMargins = (): TenantMarginSettings => {
  const {
    userInfo: { tenant },
  } = useContext(SlabContext);
  const mutation = useSlabMutation('PUT tenant settings');

  const saveTenantMargins = async (
    margins: PartialDeep<TenantSettings>,
  ): Promise<TenantSettings> => {
    return mutation.mutateAsync({
      args: {
        body: NewUpdateTenantSettingsRequest(margins),
      },
    });
  };

  // Convert to boolean for isMoneySwitchOn (true = dollar, false = percentage)
  const defaultIsMoneySwitchOn =
    tenant.marginConfiguration !== Enums.MarginConfiguration.Percentage;

  return {
    marginConfiguration: tenant.marginConfiguration,
    defaultMargins: {
      targetMarginFlatAmount: tenant.targetMarginFlatAmount,
      minimumMarginFlatAmount: tenant.minimumMarginFlatAmount,
      targetMarginPercentage: tenant.targetMarginPercentage,
      minimumMarginPercentage: tenant.minimumMarginPercentage,
    },
    defaultIsMoneySwitchOn,
    saveTenantMargins,
  };
};
